import React, { useEffect, useRef } from 'react';
import { Column } from '@antv/g2plot';
import type { ChartField } from '@/types/chat';
import { CHART_COLORS } from '@/common/constants';
import { toNum } from '@/utils';

interface ColumnChartProps {
  data: Record<string, unknown>[];
  dimensions?: ChartField[];
  metrics?: ChartField[];
}

function foldData(
  data: Record<string, unknown>[],
  xField: string,
  metricFields: ChartField[],
): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = [];
  for (const row of data) {
    for (const m of metricFields) {
      result.push({
        [xField]: row[xField],
        __value: toNum(row[m.code]),
        __type: m.name ?? m.code,
      });
    }
  }
  return result;
}

/** 单指标时直接将原始数据中的数值字段转为 number */
function normalizeData(
  data: Record<string, unknown>[],
  metricCode: string,
): Record<string, unknown>[] {
  return data.map((row) => ({ ...row, [metricCode]: toNum(row[metricCode]) }));
}

export const ColumnChart: React.FC<ColumnChartProps> = ({ data, dimensions, metrics }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Column | null>(null);

  const resolveFields = (d: Record<string, unknown>[]) => {
    const keys = d?.length ? Object.keys(d[0]) : [];
    const xField = dimensions?.[0]?.code ?? keys[0] ?? 'x';
    const metricFields: ChartField[] = metrics?.length
      ? metrics
      : keys.slice(1).map((k) => ({ code: k, name: k }));
    return { xField, metricFields };
  };

  const buildChartData = (d: Record<string, unknown>[]) => {
    const { xField, metricFields } = resolveFields(d);
    const isMultiMetric = metricFields.length > 1;
    return {
      xField,
      metricFields,
      chartData: isMultiMetric
        ? foldData(d, xField, metricFields)
        : normalizeData(d, metricFields[0]?.code ?? 'y'),
      yField: isMultiMetric ? '__value' : (metricFields[0]?.code ?? 'y'),
      seriesField: isMultiMetric ? '__type' : undefined,
    };
  };

  useEffect(() => {
    if (!containerRef.current || !data?.length) return;

    const { xField, metricFields, chartData, yField, seriesField } = buildChartData(data);
    const xTitle = dimensions?.[0]?.name ?? dimensions?.[0]?.code ?? xField;
    const yTitle = metricFields.map((m) => m.name ?? m.code).join(' & ');

    chartRef.current = new Column(containerRef.current, {
      data: chartData,
      xField,
      yField,
      ...(seriesField ? { seriesField, isGroup: true } : {}),
      autoFit: true,
      height: 260,
      color: CHART_COLORS,
      xAxis: {
        title: { text: xTitle },
        label: { autoRotate: false },
      },
      yAxis: {
        title: { text: yTitle },
        nice: true,
        tickCount: 5,
      },
      legend: seriesField ? { position: 'top' } : false,
      tooltip: { showContent: true },
    });

    chartRef.current.render();

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!chartRef.current || !data?.length) return;
    const { chartData } = buildChartData(data);
    chartRef.current.changeData(chartData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return <div ref={containerRef} style={{ height: '100%', width: '100%' }} />;
};
